import { useCallback, useMemo, useRef, useState } from 'react';

/**
 * Deferred-apply state for a list's filter drawer (NOT-391).
 *
 * List filters are server-side (search params). Historically each drawer control
 * committed on-change (`navigate({ search })`), so the list changed the instant a
 * criterion was picked and the "Apply" button was cosmetic. This hook adds the
 * missing draft layer: controls write to a local `draft`, and the URL/query only
 * changes when the page commits the draft on Apply.
 *
 * Contract:
 * - `draft` — the in-progress filter values the drawer controls bind to.
 * - `setDraft(partial)` — merge a partial update into the draft (never commits).
 * - `reset()` — reset the draft to `resetValue` (the list's cleared/default
 *   filters). Local only — nothing is committed until Apply.
 * - `apply()` — commit the current draft (the page's existing `navigate({ search })`).
 * - `seed()` — reseed the draft from the currently-committed filters. Call this
 *   when the drawer opens so reopening always reflects the applied filters and any
 *   un-applied edits are discarded.
 * - `dirty` — true when the draft differs from the committed filters.
 *
 * The committed filters remain the single source of truth for the active-filter
 * badge; the badge must keep counting `committed`, never `draft`.
 */
export interface FilterDraft<T> {
  draft: T;
  setDraft: (partial: Partial<T>) => void;
  reset: () => void;
  apply: () => void;
  seed: () => void;
  dirty: boolean;
}

function shallowEqual<T extends object>(a: T, b: T): boolean {
  if (Object.is(a, b)) return true;
  const aKeys = Object.keys(a) as (keyof T)[];
  const bKeys = Object.keys(b) as (keyof T)[];
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => {
    const av = a[key];
    const bv = b[key];
    if (Object.is(av, bv)) return true;
    // One level deeper for array-valued filters (e.g. tag id lists).
    if (Array.isArray(av) && Array.isArray(bv)) {
      return av.length === bv.length && av.every((v, i) => Object.is(v, bv[i]));
    }
    return false;
  });
}

/**
 * @param committed  The currently-applied filter values (from the route search params).
 * @param commit     Commits a draft — the page's existing `navigate({ search })`.
 * @param resetValue The cleared/default filter values `reset()` returns the draft to.
 *                   Defaults to the current `committed` when omitted.
 */
export function useFilterDraft<T extends object>(
  committed: T,
  commit: (next: T) => void,
  resetValue?: T,
): FilterDraft<T> {
  const [draft, setDraftState] = useState<T>(committed);

  // Latest values held in refs so the returned callbacks keep stable identities
  // (the committed subset is typically rebuilt each render).
  const committedRef = useRef(committed);
  committedRef.current = committed;
  const resetRef = useRef(resetValue);
  resetRef.current = resetValue;
  const commitRef = useRef(commit);
  commitRef.current = commit;
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const setDraft = useCallback((partial: Partial<T>) => {
    setDraftState((prev) => ({ ...prev, ...partial }));
  }, []);

  const seed = useCallback(() => {
    setDraftState(committedRef.current);
  }, []);

  const reset = useCallback(() => {
    setDraftState(resetRef.current ?? committedRef.current);
  }, []);

  const apply = useCallback(() => {
    commitRef.current(draftRef.current);
  }, []);

  const dirty = useMemo(() => !shallowEqual(draft, committed), [draft, committed]);

  return { draft, setDraft, reset, apply, seed, dirty };
}
